import { driverCheckPayload } from "./consolidationDriverCheck.js";
import { sheetBreaks } from "./consolidationBreaks.js";
import { visitFullyExcluded } from "./consolidationExecution.js";
import { quantityBaselines } from "./consolidationQuantityConflicts.js";

export const numberLabel = (value, digits = 6) => value == null || value === "" ? "—" : Number(value).toLocaleString("fr-CA", { maximumFractionDigits: digits });
export const moneyLabel = (value) => value == null || value === "" ? "—" : Number(value).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });

export const truckNumber = (row) => row.truck_number || "Numéro non renseigné";
// Numéro UniAg sans la date que l'import ajoute quand la ferme a déjà reçu ce produit (« -AAAAMMJJ ») : il tient sur
// une ligne ; le numéro complet reste au survol.
export const orderNumberLabel = (label) => String(label ?? "").replace(/^(UNIAG-\d+)-\d{8}$/, "$1");
export const workDateLabel = day => day ? new Intl.DateTimeFormat("fr-CA", {
  day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
}).format(new Date(`${day}T12:00:00Z`)) : "—";

// Dernier soir de semaine avant la livraison : vendredi pour un samedi, un dimanche ou un lundi, sinon la veille (preload_evening du serveur).
export const preloadEvening = (delivery) => shiftDate(delivery, [-2, -3][new Date(`${delivery}T12:00:00Z`).getUTCDay()] ?? -1);
export const preloadExecutionDate = (row) => row.execution_date || (row.delivery_date ? preloadEvening(row.delivery_date) : null);

export function dayOffsetLabel(day, baseDate) {
  const offset = Math.round((Date.parse(`${day}T12:00:00Z`) - Date.parse(`${baseDate}T12:00:00Z`)) / 86400000);
  return !Number.isFinite(offset) || offset === 0 ? "J" : `J${offset > 0 ? "+" : "−"}${Math.abs(offset)}`;
}

export function clockLabel(value, timezone, baseDate) {
  if (!value) return "—";
  const local = localDateTime(value, timezone);
  if (!local.includes("T")) return /^\d{2}:\d{2}/.test(local) ? local.slice(0, 5) : "—";
  const day = local.slice(0, 10);
  const suffix = baseDate && day !== baseDate ? ` ${dayOffsetLabel(day, baseDate)}` : "";
  return `${local.slice(11, 16)}${suffix}`;
}

export function clockValue(clock, day, timezone) {
  if (!clock) return null;
  const local = `${day}T${clock}`;
  const matches = localTimeCandidates(local, timezone);
  return matches.length === 1 ? matches[0].instant : local;
}

// Start of service as a local clock: an earlier time on the relevé falls after midnight, on the next calendar day.
export const serviceStartClock = row => localDateTime(row.service_start || row.planned_service_start, row.timezone).slice(11, 16) || null;

export function shiftDate(day, offset) {
  return new Date(Date.parse(`${day}T12:00:00Z`) + offset * 86400000).toISOString().slice(0, 10);
}

export function localDateTime(value, timezone) {
  if (!value) return "";
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(value)) return value;
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).formatToParts(new Date(value));
  const p = Object.fromEntries(parts.map(({ type, value: part }) => [type, part]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}`;
}

// Return every valid instant, including both occurrences of a repeated autumn hour.
export function localTimeCandidates(value, timezone) {
  if (!value) return [];
  const normalized = value.length === 16 ? `${value}:00` : value;
  const nominal = Date.parse(`${normalized}Z`);
  if (!Number.isFinite(nominal)) return [];
  const results = [];
  for (let offset = -14 * 60; offset <= 14 * 60; offset += 15) {
    const instant = new Date(nominal - offset * 60000).toISOString();
    if (localDateTime(instant, timezone) === normalized) results.push({ instant, label: `UTC${offset < 0 ? "−" : "+"}${String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0")}:${String(Math.abs(offset) % 60).padStart(2, "0")}` });
  }
  return results.sort((a, b) => a.instant.localeCompare(b.instant));
}

export function timestampPayload(value) {
  if (!value) return null;
  if (!/Z$|[+-]\d{2}:\d{2}$/.test(value)) throw new Error("Résolvez l’heure inexistante ou répétée avant l’enregistrement.");
  return value;
}

export function deltaMinutes(actual, planned) {
  if (!actual || !planned) return "—";
  const difference = (Date.parse(actual) - Date.parse(planned)) / 60000;
  return Number.isFinite(difference) ? `${difference > 0 ? "+" : ""}${numberLabel(difference, 1)} min` : "—";
}

// Origine Geotab des heures : proposée (en attente de sauvegarde) ou déjà enregistrée ; une retouche la retire.
const GEOTAB_SERVICE_SOURCES = { carnet: "geotab_logbook", trajet: "geotab_trip" };
export function serviceTimeSource(row, key) {
  if (!row[key]) return null;
  const saved = row[`${key}_source`];
  return GEOTAB_SERVICE_SOURCES[row.geotab_service?.[key]] || (saved === "geotab_logbook" || saved === "geotab_trip" ? saved : null);
}
const departureSource = (visit) => (visit.geotab ? "geotab" : visit.departure_source === "manual" ? "manual" : null);

export function sheetPayload(row, quantities = {}) {
  const pmSourceIds = new Set(row.visits.filter(v => v.pm?.role === "source").flatMap(v => v.orders.map(o => o.id)));
  const pmTargetIds = new Set(row.visits.filter(v => v.pm?.role === "target").flatMap(v => v.orders.map(o => o.id)));
  return {
    version: row.version, shared_versions: row.shared_versions, day_version: row.day_version,
    ...(row.pm_linked_versions ? { pm_linked_versions: row.pm_linked_versions } : {}),
    quantity_guard_versions: row.quantity_guard_versions,
    quantity_mode: row.status === "draft" ? "delivered" : row.quantity_mode,
    quantity_baselines: quantityBaselines(row, quantities),
    ...Object.fromEntries(["service_start", "service_end", "truck_departure", "lunch_start", "lunch_end"].map((key) => [key, timestampPayload(row[key])])),
    breaks: breaksPayload(row),
    ...Object.fromEntries(["service_start", "service_end"].map((key) => [`${key}_source`, serviceTimeSource(row, key)]).filter(([, source]) => source)),
    no_lunch: Boolean(row.no_lunch), notes: row.notes || null,
    execution_decisions: row.execution_decisions || [],
    // Qui a conduit (réponse du consolidateur) ; absent d'un ancien brouillon : la réponse enregistrée reste.
    ...(row.driver_check !== undefined ? { driver_check: driverCheckPayload(row.driver_check) } : {}),
    visits: row.visits.map((visit) => ({ id: visit.id, actual_sequence: Number(visit.actual_sequence), status: visit.status,
      arrival: visitFullyExcluded(row, visit) ? null : timestampPayload(visit.arrival),
      departure: visitFullyExcluded(row, visit) ? null : timestampPayload(visit.departure), notes: visit.notes || null,
      ...(departureSource(visit) ? { departure_source: departureSource(visit) } : {}) })),
    quantities: Object.entries(quantities).map(([order_id, fields]) => ({ order_id, ...Object.fromEntries(Object.entries(fields)
      .filter(([key]) => !(["loaded_tons", "delivered_tons"].includes(key) && pmSourceIds.has(order_id)) && !(key === "loaded_tons" && pmTargetIds.has(order_id)))
      .map(([key, value]) => [key, value === "" ? null : value])) })).filter(value => Object.keys(value).length > 1),
  };
}

// Une ligne de pause sans aucune heure n'est pas enregistrée.
export const breaksPayload = row => sheetBreaks(row).filter(pause => pause.start || pause.end)
  .map(pause => ({ start: timestampPayload(pause.start), end: timestampPayload(pause.end), paid: pause.paid ?? null }));

export function summaryCsv(rows) {
  // Preserve the server's decimal strings rather than the rounded screen values.
  const decimal = value => {
    if (value == null || value === "") return "";
    const raw = String(value);
    const match = raw.match(/^([+-]?)(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/);
    if (!match) return raw;
    const [, sign, integer, fraction = "", exponent = "0"] = match;
    const digits = integer + fraction;
    const point = integer.length + Number(exponent);
    const expanded = point <= 0 ? `0.${"0".repeat(-point)}${digits}`
      : point >= digits.length ? digits + "0".repeat(point - digits.length) : `${digits.slice(0, point)}.${digits.slice(point)}`;
    return (sign + expanded).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "").replace(".", ",");
  };
  const statuses = { eligible: "Disponible", blocked: "À compléter", allocated: "Dans un relevé" };
  const sources = { sheet: "Relevé", preload: "Préchargement" };
  const cell = (value) => `"${String(value ?? "").replace(/^[=+@\-]/, "'$&").replaceAll('"', '""')}"`;
  return "\uFEFF" + [["Journée", "Camion", "Entreprise facturée", "Prestation", "Unité", "Quantité", "Tarif", "Montant",
    "Chauffeur", "État", "Motifs de blocage", "Référence commande", "Date de réalisation", "Entreprise de facturation",
    "ID prestation", "ID entreprise facturée", "ID entreprise de facturation", "ID chauffeur", "Source", "ID source", "ID révision"],
    ...rows.map((row) => [row.date, row.truck_numbers?.join(", ") || row.truck_id, row.mill?.display_name || row.mill?.name, row.description, row.charge?.kind === "fixed_fee" ? "forfait" : row.unit,
      decimal(row.quantity), decimal(row.rate), decimal(row.amount), row.driver_name, statuses[row.status] || row.status,
      (row.blocking_reasons || []).map(reason => reason.message).join(" · "), row.charge?.order_label,
      row.execution_date || row.date, row.mill?.account_name, row.id, row.mill?.id, row.billing_account_id,
      row.employee_id, sources[row.source_type] || row.source_type, row.source_object_id, row.source_revision_id])].map((row) => row.map(cell).join(";")).join("\r\n");
}

export function workedSeconds(row) {
  const parse = value => value && /Z$|[+-]\d{2}:\d{2}$/.test(value) ? Date.parse(value) : NaN;
  const start = parse(row.service_start), end = parse(row.service_end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  // Le dîner et les pauses non payées ne sont pas du temps travaillé ; une pause payée l'est (sans être facturée).
  let excluded = 0;
  for (const [from, to, paid] of [[row.lunch_start, row.lunch_end], ...sheetBreaks(row).map(pause => [pause.start, pause.end, pause.paid])]) {
    if (!from && !to) continue;
    const a = parse(from), b = parse(to);
    if (!(start <= a && a < b && b <= end)) return null;
    if (paid !== true) excluded += b - a;
  }
  // Tournée imbriquée (autre camion du même chauffeur) : ses heures sont sur son propre relevé.
  for (const other of row.exclusions || []) {
    const a = parse(other.start_at), b = parse(other.end_at);
    if (other.kind === "sheet" && start <= a && a < b && b <= end) excluded += b - a;
  }
  return Math.round((end - start - excluded) / 1000);
}

// Durée arrondie à la minute, par exemple « 8 h 05 ».
export function durationLabel(minutes) {
  const total = Math.round(Math.abs(minutes));
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, "0")}`;
}

export function workedTimeLabel(row) {
  const seconds = workedSeconds(row);
  if (seconds == null) return null;
  const minutes = Math.floor(seconds % 3600 / 60);
  const remainder = seconds % 60;
  return `${Math.floor(seconds / 3600)} h ${String(minutes).padStart(2, "0")} min${remainder ? ` ${remainder} s` : ""}`;
}
